import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ShieldCheck, X } from "lucide-react";
import Button from "../ui/Button";
import Input from "../ui/Input";
import StatusBadge from "./StatusBadge";
import { USE_MOCK_AUTH } from "../../config/api";
import {
  checkPreApprovalStatus,
  fetchOpsPreApprovals,
  requestCasePreApproval,
  updateOpsPreApproval,
} from "../../services/paymentApi";
import { showError, showSuccess } from "../../utils/toast";

const NETWORKS = [
  { id: "MTN", label: "MTN" },
  // { id: "TELECEL", label: "Telecel" },
  // { id: "AIRTELTIGO", label: "AirtelTigo" },
];

function formatWhen(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function mandateFromPayload(payload) {
  return payload?.mandate || payload || null;
}

function mandateStatusCopy(status) {
  const key = String(status || "pending").toLowerCase();
  if (key === "approved" || key === "active" || key === "completed") {
    return "The customer approved this mandate. Auto-billing can use it until the end date.";
  }
  if (key === "failed" || key === "rejected" || key === "expired" || key === "cancelled") {
    return "This mandate was rejected, expired, or failed. Send a new one if the customer should approve again.";
  }
  return "Waiting for the customer to approve on their phone. Status stays pending until they accept or reject.";
}

function toDatetimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function PreApprovalModal({ caseRow, record = null, onClose, onComplete }) {
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [msisdn, setMsisdn] = useState("");
  const [network, setNetwork] = useState("MTN");
  const [endDate, setEndDate] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [checkingId, setCheckingId] = useState("");
  const [existing, setExisting] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [result, setResult] = useState(null);

  const defaultEnd = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 3);
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T23:59`;
  }, []);

  const loadExisting = async ({ force = false } = {}) => {
    if (!useLiveApi || !caseRow?.id) {
      setExisting([]);
      return;
    }
    setLoadingExisting(true);
    try {
      const rows = await fetchOpsPreApprovals(token, { case_id: caseRow.id }, { force });
      setExisting(Array.isArray(rows) ? rows : []);
    } catch {
      setExisting([]);
    } finally {
      setLoadingExisting(false);
    }
  };

  useEffect(() => {
    if (!caseRow && !record) return;
    setMsisdn(record?.msisdn || caseRow?.phone || caseRow?.customerPhone || "");
    setNetwork(record?.network || "MTN");
    setEndDate(record?.end_date ? toDatetimeLocal(record.end_date) : defaultEnd);
    setDescription(
      record?.description || `Repayment mandate for ${caseRow?.customer || record?.customer_name || caseRow?.id || ""}`
    );
    setResult(null);
    loadExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseRow, record, defaultEnd, token, useLiveApi]);

  const handleSubmit = async () => {
    if (!endDate) {
      showError("Set the mandate expiry date agreed with the customer.");
      return;
    }
    setSaving(true);
    try {
      let mandate = null;
      const body = {
        msisdn: msisdn.trim(),
        network,
        description: description.trim(),
        end_date: endDate.replace("T", " ") + ":00",
      };
      if (useLiveApi && record?.reference_id) {
        const payload = await updateOpsPreApproval(token, record.reference_id, body);
        mandate = mandateFromPayload(payload);
        setResult(mandate);
        await loadExisting({ force: true });
      } else if (useLiveApi) {
        const payload = await requestCasePreApproval(token, caseRow.id, body);
        mandate = mandateFromPayload(payload);
        setResult(mandate);
        await loadExisting({ force: true });
      } else {
        mandate = {
          reference_id: "MOCK-MANDATE",
          status: "pending",
          approval_status: "pending",
          start_date: new Date().toISOString(),
          end_date: endDate,
        };
        setResult(mandate);
      }
      showSuccess(
        record?.reference_id
          ? `Mandate ${record.reference_id} saved.`
          : mandate?.status
            ? `Mandate ${mandate.reference_id || ""} is ${mandate.status}.`
            : "Pre-approval prompt sent. The customer must approve on their phone."
      );
      onComplete?.(mandate);
    } catch (error) {
      showError(error.message || "Could not send the mandate.");
    } finally {
      setSaving(false);
    }
  };

  const handleCheck = async (row, { silent = false } = {}) => {
    if (!row?.reference_id || !useLiveApi) return;
    setCheckingId(row.reference_id);
    try {
      const payload = await checkPreApprovalStatus(token, row.reference_id);
      const mandate = mandateFromPayload(payload);
      setResult(mandate);
      await loadExisting({ force: true });
      const nextStatus = mandate?.approval_status || mandate?.status || "pending";
      if (!silent || String(nextStatus).toLowerCase() !== "pending") {
        showSuccess(`Mandate ${row.reference_id} is ${nextStatus}.`);
      }
      onComplete?.(mandate);
    } catch (error) {
      if (!silent) showError(error.message || "Could not check mandate status.");
    } finally {
      setCheckingId("");
    }
  };

  const latest = existing[0] || result;
  const latestStatus = latest?.approval_status || latest?.status || "";

  useEffect(() => {
    if (!useLiveApi || !latest?.reference_id) return undefined;
    const key = String(latestStatus || "pending").toLowerCase();
    if (key !== "pending" && key !== "") return undefined;
    const timer = window.setInterval(() => {
      handleCheck(latest, { silent: true });
    }, 12000);
    return () => window.clearInterval(timer);
    // Refresh only while this mandate is still waiting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useLiveApi, latest?.reference_id, latestStatus]);

  if (!caseRow && !record) return null;

  const subject = caseRow || {
    id: record.case_id,
    customer: record.customer_name,
    accountNumber: record.account_number || record.accountNumber,
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mandate-title"
        className="flex max-h-[96vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between border-b border-primary/[0.06] px-5 py-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
              Pre-approval mandate
            </p>
            <h2 id="mandate-title" className="mt-1 text-lg font-bold text-primary">{subject.customer}</h2>
            <p className="text-sm text-primary/50">
              Case {subject.id}
              {subject.accountNumber ? ` · ${subject.accountNumber}` : ""}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-primary/40 hover:bg-primary/5">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {latest ? (
            <div className="rounded-xl border border-primary/10 bg-primary/[0.03] px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
                  Current mandate
                </p>
                <StatusBadge status={latest.approval_status || latest.status} />
              </div>
              <p className="mt-1 text-sm font-semibold text-primary">
                {latest.reference_id || "Pending reference"}
              </p>
              <p className="mt-1 text-xs text-primary/55">
                {latest.network || "MTN"} · {latest.msisdn || "—"} · ends {formatWhen(latest.end_date)}
                {latest.initiated_by ? ` · ${latest.initiated_by}` : ""}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-primary/70">{mandateStatusCopy(latestStatus)}</p>
              {latest.last_status_checked_at ? (
                <p className="mt-1 text-[11px] text-primary/40">
                  Last checked {formatWhen(latest.last_status_checked_at)}
                </p>
              ) : null}
            </div>
          ) : null}

          <p className="text-xs text-primary/55">
            The customer approves a debit mandate on their phone. Auto-billing can only run until the
            end date you agree here.
          </p>
          <Input label="Wallet number" value={msisdn} onChange={(e) => setMsisdn(e.target.value)} />
          <div>
            <p className="mb-2 text-xs font-semibold text-primary/50">Network</p>
            <div className="grid grid-cols-3 gap-2">
              {NETWORKS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setNetwork(item.id)}
                  className={`rounded-xl border px-3 py-2 text-sm font-semibold ${
                    network === item.id
                      ? "border-accent/40 bg-accent/[0.08]"
                      : "border-primary/10"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <Input
            label="Mandate end date"
            type="datetime-local"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <div>
            <Input
              label="Description the customer sees"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <p className="mt-1.5 text-[11px] text-primary/45">
              This text is on the mandate request on their phone. The reference is created when you send.
            </p>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold text-primary/50">Mandates already sent</p>
              {loadingExisting ? <span className="text-[11px] text-primary/40">Loading…</span> : null}
            </div>
            {existing.length === 0 ? (
              <p className="rounded-xl border border-dashed border-primary/10 px-3 py-4 text-xs text-primary/45">
                No mandate has been triggered for this case yet.
              </p>
            ) : (
              <ul className="space-y-2">
                {existing.map((row) => (
                  <li
                    key={row.reference_id || row.id}
                    className="rounded-xl border border-primary/10 px-3 py-2.5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold text-primary">{row.reference_id}</p>
                        <p className="mt-0.5 text-[11px] text-primary/50">
                          {row.network || "MTN"} · {row.msisdn || "—"} · ends {formatWhen(row.end_date)}
                          {row.description ? ` · ${row.description}` : ""}
                        </p>
                      </div>
                      <StatusBadge status={row.approval_status || row.status} />
                    </div>
                    <div className="mt-2 flex justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!useLiveApi || checkingId === row.reference_id}
                        onClick={() => handleCheck(row)}
                      >
                        {checkingId === row.reference_id ? "Checking…" : "Refresh status"}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-primary/[0.06] bg-primary/[0.02] px-5 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Close
          </Button>
          <Button size="sm" onClick={handleSubmit} loading={saving}>
            <ShieldCheck className="h-4 w-4" />
            {record?.reference_id ? "Save changes" : "Send mandate"}
          </Button>
        </div>
      </div>
    </div>
  );
}
